import React from 'react';
import {
  X,
  FileText,
  NotebookPen,
  Calendar,
  Tag,
  MessageSquare,
  FolderOpen,
  Phone,
  PackageCheck,
  User
} from 'lucide-react';
import { Arquivo } from '../../types/gerArquivos';

// Mapeamento de categorias
const CATEGORIAS = {
  1: 'DTF TÊXTIL',
  2: 'DTF UV',
  3: 'SUBLIMÁTICA',
  4: 'CAMISETA'
} as const;

function getCategoriaColor(categoria: number): string {
  switch (categoria) {
    case 1: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 2: return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 3: return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 4: return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'salvo':
      return 'text-green-400 bg-green-400/10 border-green-400/30';
    case 'em_edicao':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    case 'finalizado':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
  }
}

function getStatusText(status: string) {
  switch (status) {
    case 'salvo':
      return 'Salvo';
    case 'em_edicao':
      return 'Em edição';
    case 'finalizado':
      return 'Finalizado';
    default:
      return 'Desconhecido';
  }
}

interface ArquivoDetalhesModalProps {
  arquivo: Arquivo;
  onClose: () => void;
  onAbrirArquivo: (arquivo: Arquivo) => void;
}

export function ArquivoDetalhesModal({ arquivo, onClose, onAbrirArquivo }: ArquivoDetalhesModalProps) {
  const formattedDate = new Date(arquivo.dataCriacao).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Fechar modal ao clicar no backdrop
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Fechar modal com ESC
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div className="bg-navy-800 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-navy-700 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-4 border-b border-navy-700 flex items-center justify-between bg-navy-800/50 backdrop-blur-sm sticky top-0 z-10">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Detalhes do Arquivo
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-700 rounded-lg transition-all"
            title="Fechar (ESC)"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Grid de informações principais */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Nome do Arquivo */}
            <div className="sm:col-span-2 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <FileText className="w-3.5 h-3.5" />
                <span className="font-medium">Arquivo</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-sm text-white font-mono break-all">{arquivo.arquivoNome}</p>
              </div>
            </div>

            {/* Cliente */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <NotebookPen className="w-3.5 h-3.5" />
                <span className="font-medium">Cliente</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-sm text-white">{arquivo.cliente?.nome || 'Não informado'}</p>
              </div>
            </div>

            {/* WhatsApp */}
            {arquivo.cliWhatsapp && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="font-medium">WhatsApp</span>
                </div>
                <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                  <p className="text-sm text-white">{arquivo.cliWhatsapp}</p>
                </div>
              </div>
            )}

            {/* Data/Hora */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Calendar className="w-3.5 h-3.5" />
                <span className="font-medium">Data de Criação</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-sm text-white">{formattedDate}</p>
              </div>
            </div>

            {/* Categoria */}
            {arquivo.prodCatItem && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Tag className="w-3.5 h-3.5" />
                  <span className="font-medium">Categoria</span>
                </div>
                <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-bold border ${getCategoriaColor(arquivo.prodCatItem)}`}>
                    {CATEGORIAS[arquivo.prodCatItem]}
                  </span>
                </div>
              </div>
            )}

            {/* Status */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <User className="w-3.5 h-3.5" />
                <span className="font-medium">Status</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-bold border ${getStatusColor(arquivo.status)}`}>
                  {getStatusText(arquivo.status)}
                </span>
              </div>
            </div>

            {/* ID do Arquivo */}
            {arquivo.id && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Tag className="w-3.5 h-3.5" />
                  <span className="font-medium">ID</span>
                </div>
                <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                  <p className="text-sm text-gray-300 font-mono">{arquivo.id}</p>
                </div>
              </div>
            )}
          </div>

          {/* Observações (full width se existir) */}
          {arquivo.observacoes && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="font-medium">Observações</span>
              </div>
              <div className="p-4 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
                  {arquivo.observacoes}
                </p>
              </div>
            </div>
          )}

          {/* Caminho do Arquivo (full width se disponível) */}
          {arquivo.arquivoCaminho && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <FolderOpen className="w-3.5 h-3.5" />
                <span className="font-medium">Caminho Completo</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-xs text-gray-400 font-mono break-all leading-relaxed">
                  {arquivo.arquivoCaminho}
                </p>
              </div>
            </div>
          )}

          {/* Diretório Base (se disponível) */}
          {arquivo.dirbase && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <FolderOpen className="w-3.5 h-3.5" />
                <span className="font-medium">Diretório Base</span>
              </div>
              <div className="p-3 bg-navy-900/50 rounded-lg border border-navy-700">
                <p className="text-xs text-gray-400 font-mono break-all">
                  {arquivo.dirbase}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer com Ações */}
        <div className="p-4 border-t border-navy-700 bg-navy-800/50 backdrop-blur-sm flex items-center justify-end gap-2 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-navy-700 hover:bg-navy-600 text-gray-300 hover:text-white rounded-lg text-sm transition-all"
          >
            Fechar
          </button>
          <button
            onClick={() => {
              onAbrirArquivo(arquivo);
              onClose();
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
          >
            <PackageCheck className="w-4 h-4" />
            Abrir no CorelDRAW
          </button>
        </div>
      </div>
    </div>
  );
}
